import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import MailLockIcon from "@mui/icons-material/MailLock";

import CustomHeader from "../../components/common/CustomHeader";
import CustomInput from "../../components/common/CustomInput";
import CustomButton from "../../components/common/CustomButton";
import CustomSnackbar from "../../components/common/CustomSnackbar";


import { interviewApi } from "../../api/interviewApi"; // ✅ centralized API import
import "../../style/EmailVarification.css";
import { candidateApi } from "../../api/candidateApi";
import { useAuth } from "../../contexts/AuthContext";

const INITIAL_BLOCK_STATE = { isBlocked: false, timeRemaining: 0, type: "" };

export default function EmailVerification() {
  const { interview_id, recruiter_id } = useParams();
  const navigate = useNavigate();
  const { setAuthData } = useAuth();
  const hasFetchedRef = useRef(false);

  /** ------------------ State ------------------ **/
  const [candidate, setCandidate] = useState({ id: "", email: "" });
  const [otp, setOtp] = useState("");
  const [interviewStatus, setInterviewStatus]=useState("")
  const [requestsRemaining, setRequestsRemaining] = useState(3);
  const [attemptsRemaining, setAttemptsRemaining] = useState(4);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [mode, setMode]=useState("")
  const [block, setBlock] = useState(INITIAL_BLOCK_STATE);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({ resend: false, verify: false });
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

    /** ------------------ Helpers ------------------ **/
    const maskEmail = (email) => {
        if (!email.includes("@")) return email;
        const [user, domain] = email.split("@");
        const masked =
            user.length <= 3
                ? user[0] + "*".repeat(user.length - 1)
                : user[0] + "*".repeat(user.length - 3) + user.slice(-2);
        return `${masked}@${domain}`;
    };

    const formatTime = (seconds) =>
        `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, "0")}`;

  const startBlockTimer = (seconds, type) =>
    setBlock({ isBlocked: true, timeRemaining: seconds, type });

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  /** ------------------ API Logic ------------------ **/
const interviewValidation = useCallback(async () => {
    try {
        const response = await candidateApi.validatelink({
            interview_id,
            recruiter_id,
        });

        // console.log("Interview validation response:", response);
        const { status, message, data } = response || {};

        /* ✅ Success */
        if (status === 200) {
            setInterviewStatus(data.interview_status)
            return true;
        }

        /* ❌ Handled failures */
        let reason = "default";

        if (status === 401) {
            reason = /expired/i.test(message)
                ? "expired_link"
                : "already_completed";
        } else if (status === 403) {
            reason = "tab_switch_exceeded";
        } else if (status === 404) {
            reason = "invalid_interview";
        } else if (status === 400) {
            reason = "invalid_request";
        } else if (status === 0) {
            reason = "network_error";
        }

        navigate(
            status === 401 || status === 403
                ? "/restriction"
                : "/error",
            {
                state: { reason, message },
                replace: true,
            }
        );
        return false;

    } catch (error) {
        console.error("Interview validation error:", error);

        navigate("/error", {
            state: {
                reason: "unexpected_error",
                message: "Something went wrong. Please try again.",
            },
            replace: true,
        });
        return false;
    }
}, [interview_id, recruiter_id, navigate]);


const requestEmailVerification = async ({
  interview_id,
  recruiter_id,
  onSuccess,
  onError,
  successMessage,
  errorMessage,
}) => {
  try {
    const response = await interviewApi.emailVerification(
      interview_id,
      recruiter_id
    );
    // console.log("Email verification API response:", response);
    const { status, message, data } = response || {};

    /* 1️⃣ Network error */
    if (status === 0) {
      showSnackbar(
        "Network error. Please check your internet connection.",
        "warning"
      );
      return;
    }

    /* 2️⃣ Server errors */
    if (status >= 500) {
      showSnackbar(
        message || errorMessage || "Server error.",
        "error"
      );
      return;
    }

    /* 3️⃣ Client / validation errors */

    if (status === 429) {
  const retryAfter = data?.retry_after_seconds ?? 600;

  showSnackbar(
    message || "Too many OTP requests. Please wait.",
    "error"
  );

  // ⏳ Start FE block timer
  startBlockTimer(retryAfter, "request");
  return;
}

    if (status >= 400) {
      showSnackbar(
        message || errorMessage || "Request failed.",
        "error"
      );
      return;
    }

    /* 4️⃣ Success */
    if (status === 200 && data) {
      onSuccess?.(data);
      showSnackbar(
        message || successMessage || "OTP sent successfully!",
        "success"
      );
      return;
    }

    /* 5️⃣ Fallback */
    showSnackbar("Unexpected response from server.", "error");

  } catch (error) {
    console.error("Email verification error:", error);
    onError?.(error);
    showSnackbar(
      errorMessage || "Something went wrong.",
      "error"
    );
  }
};

const fetchEmailVerification = async () => {
  try {
    setLoading(true);

    await requestEmailVerification({
      interview_id,
      recruiter_id,

      successMessage: "OTP sent successfully!",

      onSuccess: (data) => {
        const {
          candidate_id,
          candidate_email,
          requests_remaining,
          mode, 
        } = data;

        setCandidate({
          id: candidate_id,
          email: candidate_email,
        });

        setRequestsRemaining(requests_remaining ?? 3);
        setMode(mode); // 👈 FE state for UI logic  
        // console.log("resend onSuccess data:", data);
    },
    });

  } finally {
    setLoading(false);
  }
};


const resendOtp = async () => {
  if (block.isBlocked && block.type === "request") {
    showSnackbar(
      `⏳ Wait ${formatTime(block.timeRemaining)} to resend.`,
      "warning"
    );
    return;
  }

  if (requestsRemaining <= 0) {
    showSnackbar("No more OTP requests available.", "error");
    return;
  }

  setActionLoading((prev) => ({ ...prev, resend: true }));

  try {
    await requestEmailVerification({
      interview_id,
      recruiter_id,

      successMessage: "New OTP sent successfully!",
      errorMessage: "Unable to resend OTP.",

      onSuccess: (data) => {
  setCandidate({ id: data.candidate_id, email: data.candidate_email });
  setRequestsRemaining(data.requests_remaining ?? requestsRemaining);
  setAttemptsRemaining(4);
  setOtp("");

         
      },
    });

  } finally {
    setActionLoading((prev) => ({ ...prev, resend: false }));
  }
};


    const handleApiError = (error, type) => {
        const status = error.response?.status;
        const message = error.response?.data?.message;

    if (status === 429) {
      const match = message?.match(/(\d+) minutes and (\d+) seconds/);
      const seconds = match ? parseInt(match[1]) * 60 + parseInt(match[2]) : 600;
      showSnackbar(message || "Too many attempts. Please wait.", "error");
      startBlockTimer(seconds, type);
    } else if (status === 404) {
      showSnackbar(message || "Invalid OTP", "error");
      const match = message?.match(/Attempts remaining: (\d+)/);
      if (match) setAttemptsRemaining(parseInt(match[1]));
    } else {
      showSnackbar("Something went wrong. Please try again later.", "error");
    }
  };







  const verifyOtp = async () => {
    if (otp.length !== 6) {
      showSnackbar("Enter a valid 6-digit OTP.", "error");
      return;
    }
    if (block.isBlocked && block.type === "verification") {
      showSnackbar(`Wait ${formatTime(block.timeRemaining)} to verify again.`, "warning");
      return;
    }
    if (attemptsRemaining <= 0) {
      showSnackbar("No verification attempts left.", "error");
      return;
    }

    setActionLoading((prev) => ({ ...prev, verify: true }));
    try {
    setActionLoading((prev) => ({ ...prev, verify: true }));

    const response = await interviewApi.verifyOtp({
        candidateId: candidate.id,
        otp,
        interview_id,
        recruiter_id,
    });
    // console.log("Verify OTP response:", response);
    const { status, message, data } = response || {};

    /* 1️⃣ Network error */
    if (status === 0) {
        showSnackbar(
            "Network error. Please check your internet connection.",
            "warning"
        );
        return;
    }

    /* 2️⃣ Server errors */
    if (status >= 500) {
        showSnackbar(
            message || "Server error while verifying OTP.",
            "error"
        );
        return;
    }

    /* 3️⃣ Client / auth errors */
    if (status >= 400) {
        showSnackbar(
            message || "Invalid or expired OTP.",
            "error"
        );
        return;
    }

    /* 4️⃣ Success */
    if (status === 200 && data) {
        // sessionStorage.setItem(
        //     "Interview_status",
        //     data.interview_status
        // );

        showSnackbar(
            message || "OTP verified successfully!",
            "success"
        );

        setBlock(INITIAL_BLOCK_STATE);
        setIsOtpVerified(true);
        
        setAuthData({
            interview_id,
            recruiter_id,
            candidate_id: candidate.id,
            interview_status: interviewStatus,
            interview_mode: mode
        });

        navigate("/interview/instruction",{
            state:{
                "mode": mode, // 👈 FE state for UI logic
                "candidate_id":candidate.id,
                "Interview_status": interviewStatus,
            }
        });
        return;
    }

    /* 5️⃣ Fallback */
    showSnackbar(
        "Unexpected response from server.",
        "error"
    );

} catch (error) {
    console.error("OTP verification error:", error);
    showSnackbar(
        "Something went wrong while verifying OTP.",
        "error"
    );
} finally {
    setActionLoading((prev) => ({ ...prev, verify: false }));
}

  };

    /** ------------------ Effects ------------------ **/
    useEffect(() => {
        if (!hasFetchedRef.current) {
            hasFetchedRef.current = true;
            (async () => {
                const isValid = await interviewValidation();
                if (isValid) fetchEmailVerification();
            })();
        }
    }, [interviewValidation]);

    // useEffect(() => {
    //     if (block.isBlocked && block.timeRemaining > 0) {
    //         const timer = setTimeout(
    //             () => setBlock((prev) => ({ ...prev, timeRemaining: prev.timeRemaining - 1 })),
    //             1000
    //         );
    //         return () => clearTimeout(timer);
    //     }
    // }, [block]);

    useEffect(() => {
  if (!block.isBlocked) return;

  if (block.timeRemaining <= 0) {
    // ⏳ Timer finished → unblock
    setBlock(INITIAL_BLOCK_STATE);
    return;
  }

  const timer = setTimeout(() => {
    setBlock((prev) => ({
      ...prev,
      timeRemaining: prev.timeRemaining - 1,
    }));
  }, 1000);

  return () => clearTimeout(timer);
}, [block]);


    {block.isBlocked && (
  <Alert severity="warning">
    OTP requests blocked for {formatTime(block.timeRemaining)}
  </Alert>
)}

  /** ------------------ UI ------------------ **/
  if (loading)
    return (
      <Box className="loading-container">
        <CircularProgress sx={{ color: "#757575" }} />
      </Box>
    );

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <CustomHeader title="Email Verification Page" />
      <Box sx={{ mt: 10, display: "flex", justifyContent: "center" }}>
        {!isOtpVerified && (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 420,
              width: "100%",
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <MailLockIcon sx={{ mr: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                Email Verification
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" mb={1}>
              OTP sent to:
            </Typography>
            <Typography variant="body1" fontWeight="bold" mb={2}>
              {maskEmail(candidate.email)}
            </Typography>

            {block.isBlocked && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {block.type === "request" ? "OTP requests" : "Verification"}{" "}
                blocked for {formatTime(block.timeRemaining)}
              </Alert>
            )}

            <CustomInput
              label="OTP"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              placeholder="Enter 6-digit OTP"
              error={otp.length > 0 && otp.length < 6} 
                helperText={
    otp.length > 0 && otp.length < 6
      ? "OTP must be 6 digits"
      : ""
  }

              disabled={block.isBlocked && block.type === "verification"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  verifyOtp();
                }
              }}
            />

            <Box display="flex" justifyContent="space-between" mt={3}>
              <CustomButton
                onClick={resendOtp}
                disabled={
                  actionLoading.resend ||
                  requestsRemaining <= 0 ||
                  (block.isBlocked && block.type === "request")
                }
                sx={{
                  width: "48%",
                }}
              >
                {actionLoading.resend ? "Sending..." : "Resend OTP"}
                
              </CustomButton>

              <CustomButton
                onClick={verifyOtp}
                disabled={
                  otp.length !== 6 ||
                  actionLoading.verify ||
                  attemptsRemaining <= 0 ||
                
                  (block.isBlocked && block.type === "verification") 
                  // (block.isBlocked && block.type === "request") // ✅ ALSO BLOCK WHEN RESEND BLOCKED
                }
                sx={{
                  width: "48%",
                }}
              >
                {actionLoading.verify ? "Verifying..." : "Verify OTP"}
              </CustomButton>
            </Box>
          </Paper>
        )}
      </Box>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={6000}
      />
    </Box>
  );
}




























// import React, { useEffect, useState, useRef, useCallback } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import {
//   Box,
//   Typography,
//   CircularProgress,
//   Alert,
//   Paper,
// } from "@mui/material";
// import MailLockIcon from "@mui/icons-material/MailLock";

// import CustomHeader from "../../components/common/CustomHeader";
// import CustomInput from "../../components/common/CustomInput";
// import CustomButton from "../../components/common/CustomButton";
// import CustomSnackbar from "../../components/common/CustomSnackbar";
// import axiosInstance from "../../utils/axios/axios";


// import { interviewApi } from "../../api/interviewApi"; // ✅ centralized API import
// import "../../style/EmailVarification.css";

// const INITIAL_BLOCK_STATE = { isBlocked: false, timeRemaining: 0, type: "" };

// export default function EmailVerification() {

//     const navigate = useNavigate();

//     const interview_id = "aa1f0f86-1652-4cfc-a8be-e371ae9614a1"
//     const candidate_id = "6ae47542-bf6f-4be5-a589-ee0283d01dba"
//     const recruiter_id ="8cf02a64-bf13-47bf-a093-169cd4df3b0f"
//     const Interview_status = "SCHEDULED"

//       sessionStorage.setItem("interview_id", interview_id);
//       sessionStorage.setItem("candidate_id", candidate_id);
//       sessionStorage.setItem("recruiter_id", recruiter_id);
//       sessionStorage.setItem("mode","basic"); 
//       sessionStorage.setItem("Interview_status", Interview_status);

//     useEffect(()=>navigate("/interview"),[])
      



//   return (
//     <Box sx={{minHeight: "100vh" }}>
//     </Box>
//   );
// }





//   const fetchEmailVerification = async () => {
//   try {
//     setLoading(true);
//     // console.log("In the method [fetchEmailVerification]");

//     const response = await interviewApi.emailVerification(interview_id,recruiter_id);
//     // console.log("Email verification response:", response);  
//     const { status, message, data } = response || {};

//     /* 1️⃣ Network error */
//     if (status === 0) {
//       showSnackbar(
//         "Network error. Please check your internet connection.",
//         "warning"
//       );
//       return;
//     }

//     /* 2️⃣ Server errors */
//     if (status >= 500) {
//       showSnackbar(
//         message || "Server error while sending OTP.",
//         "error"
//       );
//       return;
//     }

//     /* 3️⃣ Client / validation errors */
//     if (status >= 400) {
//       showSnackbar(
//         message || "Unable to send OTP.",
//         "error"
//       );
//       return;
//     }

//     /* 4️⃣ Success */
//     if (status === 200 && data) {
//       const {
//         interview_id: iid,
//         candidate_id,
//         recruiter_id: rid,
//         candidate_email,
//         requests_remaining,
//         mode,
//       } = data;

//     //   sessionStorage.setItem("interview_id", iid);
//     //   sessionStorage.setItem("candidate_id", candidate_id);
//     //   sessionStorage.setItem("recruiter_id", rid);
//     //   sessionStorage.setItem("mode", mode);

//       setCandidate({
//         id: candidate_id,
//         email: candidate_email,
//       });

//       setRequestsRemaining(requests_remaining ?? 3);

//       showSnackbar(
//         message || "OTP sent successfully!",
//         "success"
//       );
//       return;
//     }

//     /* 5️⃣ Fallback */
//     showSnackbar("Unexpected response from server.", "error");

//   } catch (error) {
//     console.error("Email verification error:", error);
//     showSnackbar(
//       "Something went wrong while requesting OTP.",
//       "error"
//     );
//   } finally {
//     setLoading(false);
//   }
// };

////////////////////////////////////////////////////

//   const resendOtp = async () => {
//     if (block.isBlocked && block.type === "request") {
//       showSnackbar(`⏳ Wait ${formatTime(block.timeRemaining)} to resend.`, "warning");
//       return;
//     }
//     if (requestsRemaining <= 0) {
//       showSnackbar("No more OTP requests available.", "error");
//       return;
//     }

//     setActionLoading((prev) => ({ ...prev, resend: true }));
//     try {
//         setActionLoading((prev) => ({ ...prev, resend: true }));

//         const response = await interviewApi.emailVerification(
//             interview_id,
//             recruiter_id
//         );

//         // console.log("Resend OTP response:", response);

//         const { status, message, data } = response || {};

//         /* 1️⃣ Network error */
//         if (status === 0) {
//             showSnackbar(
//                 "Network error. Please check your internet connection.",
//                 "warning"
//             );
//             return;
//         }

//         /* 2️⃣ Server errors */
//         if (status >= 500) {
//             showSnackbar(
//                 message || "Server error while resending OTP.",
//                 "error"
//             );
//             return;
//         }

//         /* 3️⃣ Client / validation errors */
//         if (status >= 400) {
//             showSnackbar(
//                 message || "Unable to resend OTP.",
//                 "error"
//             );
//             return;
//         }

//         /* 4️⃣ Success */
//         if (status === 200 && data) {
//             setRequestsRemaining(data.requests_remaining ?? 0);
//             setAttemptsRemaining(4);
//             setOtp("");

//             showSnackbar(
//                 message || "New OTP sent successfully!",
//                 "success"
//             );
//             return;
//         }

//         /* 5️⃣ Fallback */
//         showSnackbar("Unexpected response from server.", "error");

// } catch (error) {
//     console.error("Resend OTP error:", error);
//     showSnackbar(
//         "Something went wrong while resending OTP.",
//         "error"
//     );
// } finally {
//     setActionLoading((prev) => ({ ...prev, resend: false }));
// }
//   };